import { useState } from "react";
import {
  signInWithPopup,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  reauthenticateWithCredential,
  updatePassword,
  updateProfile,
} from "firebase/auth";
import { auth, popupResolverReady } from "../lib/firebase";
import { warmUpPopupResolverNow } from "../lib/popupResolverWarmUp";
import { wantsLoginView } from "../lib/pageRouting";
import { clearPerUserStorage } from "../lib/userSession";
import { API_BASE_URL, fetchWithTimeout } from "../lib/api";
import { sendExtensionMessage } from "../lib/extensionMessaging";
import { SubscriptionStatus, UserInfo } from "../types";
import { trackSignup, trackLogin } from "../lib/analytics";
import { changePasswordError, emailAuthError, popupSignInError } from "../lib/authErrors";
import { accountProvider } from "../lib/accountProvider";
import { sendVerificationEmail } from "../lib/emailVerification";

export function useAuth() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  // ENG-2734: `?view=login` opens the log-in form instead. Read as the INITIAL value rather than
  // corrected by an effect, so a deep link never flashes the sign-up form first.
  const [isSignUp, setIsSignUp] = useState(() => !wantsLoginView());
  const [justLoggedOut, setJustLoggedOut] = useState(false);

  /**
   * ENG-3625: on `auth/popup-blocked`, each popup handler below runs the warm-up again. The blocked call
   * has loaded the popup sign-in iframe itself, and this brings `popupResolverReady` up to date, so a
   * second block reads as the browser's popup setting rather than "press again" a second time.
   */
  const handleGoogleLogin = async (): Promise<{ idToken: string; user: any } | null> => {
    setLoading(true);
    const resolverWasReady = popupResolverReady();
    try {
      const provider = new GoogleAuthProvider();
      // ENG-3625: auth is initialized without a resolver (lib/firebase.ts), so every popup names it.
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      const idToken = await result.user.getIdToken();
      const user = result.user;

      setUserInfo({
        name: user.displayName || "User",
        email: user.email || "N/A",
        photoURL: user.photoURL || null,
        provider: accountProvider(user.providerData),
      });

      // Track signup vs login — if creation time equals last sign-in, it's a new user
      const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
      if (isNewUser) {
        trackSignup('google');
      } else {
        trackLogin('google');
      }

      return { idToken, user };
    } catch (error: unknown) {
      const code = error instanceof Object && 'code' in error ? (error as { code: string }).code : '';
      if (code === "auth/popup-blocked") warmUpPopupResolverNow();
      setError(popupSignInError(code, error instanceof Error ? error.message : String(error), "Google", resolverWasReady));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async (): Promise<{ idToken: string; user: any } | null> => {
    setLoading(true);
    const resolverWasReady = popupResolverReady();
    try {
      const provider = new OAuthProvider("microsoft.com");
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      const idToken = await result.user.getIdToken();
      const user = result.user;

      setUserInfo({
        name: user.displayName || "User",
        email: user.email || "N/A",
        photoURL: user.photoURL || null,
        provider: accountProvider(user.providerData),
      });

      // Track signup vs login — if creation time equals last sign-in, it's a new user
      const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
      if (isNewUser) {
        trackSignup('microsoft');
      } else {
        trackLogin('microsoft');
      }

      return { idToken, user };
    } catch (error: unknown) {
      const code = error instanceof Object && 'code' in error ? (error as { code: string }).code : '';
      if (code === "auth/popup-blocked") warmUpPopupResolverNow();
      setError(popupSignInError(code, error instanceof Error ? error.message : String(error), "Microsoft", resolverWasReady));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async (): Promise<{ idToken: string; user: any } | null> => {
    setLoading(true);
    const resolverWasReady = popupResolverReady();
    try {
      // Apple only returns the user's name on the FIRST authorization, and may return a
      // private-relay email (`@privaterelay.appleid.com`) if they choose to hide it — so
      // displayName/email can be blank on subsequent sign-ins. The `email` and `name`
      // scopes ask Apple for both when it's willing to share.
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      const idToken = await result.user.getIdToken();
      const user = result.user;

      setUserInfo({
        name: user.displayName || "User",
        email: user.email || "N/A",
        photoURL: user.photoURL || null,
        provider: accountProvider(user.providerData),
      });

      // Track signup vs login — if creation time equals last sign-in, it's a new user
      const isNewUser = user.metadata.creationTime === user.metadata.lastSignInTime;
      if (isNewUser) {
        trackSignup('apple');
      } else {
        trackLogin('apple');
      }

      return { idToken, user };
    } catch (error: unknown) {
      const code = error instanceof Object && 'code' in error ? (error as { code: string }).code : '';
      if (code === "auth/popup-blocked") warmUpPopupResolverNow();
      setError(popupSignInError(code, error instanceof Error ? error.message : String(error), "Apple", resolverWasReady));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (): Promise<{ idToken: string; user: any } | null> => {
    setLoading(true);
    setError(null);

    if (isSignUp && (!firstName.trim() || !lastName.trim())) {
      setError("Please provide your first and last name.");
      setLoading(false);
      return null;
    }

    try {
      const authFunc = isSignUp
        ? createUserWithEmailAndPassword
        : signInWithEmailAndPassword;

      const result = await authFunc(auth, email, password);
      let idToken = await result.user.getIdToken();
      const user = result.user;

      if (isSignUp && firstName && lastName) {
        const displayName = `${firstName} ${lastName}`;
        await updateProfile(user, { displayName });
        // Force refresh to include displayName in token claims for subsequent API calls
        idToken = await user.getIdToken(true);
        setUserInfo({
          name: displayName,
          email: user.email || "N/A",
          photoURL: user.photoURL || null,
          provider: 'password',
        });

        // Track signup with email
        trackSignup('email');
        // ENG-3705: best effort, since the name step can resend and a failed send must not fail sign-up.
        void sendVerificationEmail().catch(() => {});
      } else {
        setUserInfo({
          name: user.displayName || "User",
          email: user.email || "N/A",
          photoURL: user.photoURL || null,
          provider: accountProvider(user.providerData),
        });

        // Track login with email
        trackLogin('email');
      }

      setPassword("");
      return { idToken, user };
    } catch (error: unknown) {
      const code = error instanceof Object && 'code' in error ? (error as { code: string }).code : '';
      setError(emailAuthError(code, isSignUp));
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ENG-4072: Log Out here signs the WEBSITE out and nothing else. It used to send `logout-complete` to
  // every detected extension, which ran the extension's full teardown; the extension's session is its
  // own now (a confirmed Log out in its popup ends it; so does deleting the account, which is the one
  // remaining `logout-complete` sender, DashboardLayout). Called from the account menu and from the
  // claim flow's "Use a different account", which both mean "this website, as someone else". The
  // extension parameters are kept so the callers do not change shape; nothing reads them.
  const handleLogout = async (
    _getExtensionId?: () => string | null,
    _getAllExtensionIds?: () => string[]
  ): Promise<void> => {
    try {
      await auth.signOut();

      // ENG-3411: through lib/safeStorage. Inside this try, a throw where site data is blocked skipped
      // everything below it, so the form kept the last person's name and email after Log Out.
      // ENG-2387: the active-tenant selection is per-user too. Left behind, the next person to sign in
      // on this tab would send a tenant they may not belong to.
      // ENG-3406: App's auth listener clears the same keys on every sign-out, including the ones that
      // do not come through here; this stays so Log Out does not depend on the listener's timing.
      clearPerUserStorage();
      setUserInfo(null);
      setJustLoggedOut(true);
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setError(null);
      setIsSignUp(false);
    } catch {
      // Sign-out failed silently
    }
  };

  /**
   * ENG-3410: the backend gives every address the same answer, whether or not it has an account and
   * however that account signs in, so a success has nothing in it to show. This used to put "This
   * account uses Google Sign-In" on screen for anyone who typed a Google user's address. The Google
   * hint now goes in the email, which only the owner of the address reads.
   *
   * A 429 (too many requests from this network, or too many at once) carries its own message. Too
   * many requests for one address is not a 429: the backend answers it like any other.
   */
  const handleForgotPassword = async (): Promise<boolean> => {
    if (!email.trim()) {
      setError("Please enter your email address first.");
      return false;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (response.ok) return true;
      const data = await response.json().catch(() => ({}));
      setError(data.error || "Failed to send reset email. Please try again.");
      return false;
    } catch (err: unknown) {
      setError("Failed to send reset email. Please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const sendPasswordResetEmail = async (emailAddress: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetchWithTimeout(`${API_BASE_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddress.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to send reset email.' };
      }
    } catch {
      return { success: false, error: 'Failed to send reset email. Please try again.' };
    }
  };

  /**
   * ENG-3413: a password change ends the account's other sessions within the hour, and the Haven
   * extension in this browser holds one of them: the console gave it the console's own refresh token.
   * The SDK has just given THIS session new tokens, so they go on to every extension this page knows
   * of, in the same `auth-complete` a sign-in sends. Without it the extension found out only when its
   * next refresh failed, which it shows nobody (see lib/passwordChange.ts, and why "within the hour").
   *
   * `extensions` is what the page knows of them: the ids it has found, and the operator's own status,
   * which every push carries. Best-effort like every other push: the change has succeeded whether or
   * not an extension answers.
   */
  const handleChangePassword = async (
    currentPassword: string,
    newPassword: string,
    extensions?: { ids: string[]; subscriptionPlan: SubscriptionStatus | null },
  ): Promise<{ success: boolean; error?: string }> => {
    const user = auth.currentUser;
    if (!user || !user.email) return { success: false, error: 'Sign in again to change your password.' };

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    } catch (error: unknown) {
      const code = error instanceof Object && 'code' in error ? (error as { code: string }).code : '';
      return { success: false, error: changePasswordError(code) };
    }

    if (extensions && extensions.ids.length > 0) {
      try {
        const idToken = await user.getIdToken();
        for (const id of extensions.ids) {
          void sendExtensionMessage(id, {
            action: "auth-complete",
            idToken,
            refreshToken: user.refreshToken,
            user: { displayName: user.displayName, email: user.email, photoURL: user.photoURL, uid: user.uid },
            _tokenResponse: { idToken },
            // ENG-2387: the operator's OWN tenant, never the one selected in the console.
            subscriptionPlan: extensions.subscriptionPlan || null,
          });
        }
      } catch {
        // No token to send. The 45-minute refresh in useExtension tries again while this tab is open.
      }
    }
    return { success: true };
  };

  const toggleSignUp = () => {
    setIsSignUp(!isSignUp);
    setError(null);
    setFirstName("");
    setLastName("");
  };

  return {
    userInfo,
    setUserInfo,
    error,
    setError,
    loading,
    authInitialized,
    setAuthInitialized,
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isSignUp,
    setIsSignUp,
    justLoggedOut,
    setJustLoggedOut,
    handleGoogleLogin,
    handleMicrosoftLogin,
    handleAppleLogin,
    handleEmailAuth,
    handleForgotPassword,
    handleChangePassword,
    sendPasswordResetEmail,
    handleLogout,
    toggleSignUp,
  };
}
